import styled from "@emotion/styled";
import {
  GelBoxLayout,
  GelContainerLite,
  GelHeading3,
  GelIcon,
  GelCaption,
  GelParagraph,
  GelLink,
  GelScreenDetectorContext,
} from "@tal-gel/components";
import { getGelTokens } from "@tal-gel/theming";
import Pie from "components/components/Pie";
import TransitionComponent from "components/components/Transition";
import useIntersect from "hooks/useIntersect";
import { useContext } from "react";
import PeopleMale from "@tal-gel/theming-plugin-tal/dist/lite/brand-icons/people-male";

const scan = [
  {
    title: "SORE",
    description:
      "A spot which is sore (scaly, itchy, bleeding or tender) and does not heal within 6 weeks",
  },
  {
    title: "CHANGING",
    description: "In appearance (size, shape, colour or texture)",
  },
  {
    title: "ABNORMAL",
    description:
      "Looks different, feels different or stands out when compared to other spots and moles",
  },
  {
    title: "NEW",
    description: "Spots that have appeared recently",
  },
];

const HomeInfographics2023 = () => {
  const { isLargerThanSmScreen, screen } = useContext(GelScreenDetectorContext);
  const [ref, entry] = useIntersect({ threshold: 1 });

  return (
    <section ref={ref} style={{ height: "100%" }}>
      <GelContainerLite
        style={{
          backgroundColor: getGelTokens().global.themeColorBackgroundInverse,
        }}
      >
        <GelContainerLite fixed>
          <GelHeading3
            inverse
            textAlign="center"
            paddingTop={56}
            marginBottom={getGelTokens().global.sizeBaseX4}
          >
            Do you know how to self-check your skin?
          </GelHeading3>
          <GelParagraph inverse textAlign="center">
            With a continued focus on better understanding Australians' behaviour regarding skin
            safety, TAL has commissioned new research with the aim of promoting the importance of
            skin safety. The findings confirm there is still more work to do in raising awareness
            around skin cancer prevention. Through TAL SpotChecker, we will continue to help educate
            Australians how to self-check their skin, and how prevention and early detection can
            support in achieving better health outcomes.
          </GelParagraph>
          {/* <SpotlightContainer>
            <Stat1
              opacity={entry.isIntersecting ? 1 : 0}
              iconSize={isLargerThanSmScreen(screen) ? 88 : 72}
            />
          </SpotlightContainer> */}

          <InfographicContainer>
            <Stat2 inverse value={80} />
            <Stat1 iconSize={isLargerThanSmScreen(screen) ? 88 : 72} />
          </InfographicContainer>

          <GelParagraph inverse textAlign="center" marginBottom={getGelTokens().global.sizeBaseX8}>
            It is important to perform regular self-checks on your skin to monitor for any unusual
            changes or signs of skin cancer. It is recommended that you "SCAN" your skin - meaning
            to check for SORE, CHANGING, ABNORMAL and NEW spots or moles. Below is a diagram guide
            of the key areas to check on your body as skin cancer may develop in areas not typically
            exposed to the sun.
          </GelParagraph>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <img
              alt="SCAN diagram"
              width={isLargerThanSmScreen(screen) ? "70%" : "100%"}
              src={process.env.PUBLIC_URL + "/images/info-self-check.png"}
            />
          </div>

          <GelParagraph inverse textAlign="center" marginTop={32} marginBottom={56}>
            <GelLink
              inverse
              href="https://www.skincancercollege.org/"
              target="_blank"
              style={{ color: getGelTokens().global.themeColorTextInverse }}
            >
              The Skin Cancer College of Australasia
            </GelLink>{" "}
            recommends that you{" "}
            <span style={{ color: getGelTokens().brand.brandColorPrimary1 }}>
              <b>SCAN </b>
            </span>
            your skin looking for spots or moles that are:
          </GelParagraph>

          <GelBoxLayout gutter="xlarge">
            {scan.map((item, i) => {
              return <Acronym title={item.title}>{item.description}</Acronym>;
            })}
          </GelBoxLayout>

          <GelCaption inverse condensed textAlign="center" paddingBottom={56}>
            All stats in this image refer to the research completed by Edentify
            <sup>5</sup>.
          </GelCaption>
        </GelContainerLite>
      </GelContainerLite>
    </section>
  );
};

export default HomeInfographics2023;

const SpotlightContainer = styled.div({
  marginTop: 56,
  paddingTop: 56,
  paddingBottom: 80,
});

const InfographicContainer = styled.div({
  display: "grid",

  paddingTop: 56,
  paddingBottom: 56,
  gridTemplateColumns: "repeat(2, 1fr)",
  gridAutoRows: "1fr",
  gap: "0px 0px",
  gridTemplateAreas: `
          ". ."
         `,
  justifyContent: "center",
  alignContent: "center",
  justifyItems: "center",
  alignItems: "center",

  "> div": {
    borderBottom: 0,
    width: "100%",
    padding: 40,
    minHeight: 332,

    "&:nth-of-type(2n-1)": {
      borderRight: `1px solid ${getGelTokens().global.themeColorBorderDefault}`,
    },
  },

  "@media (max-width: 576px)": {
    display: "flex",
    flexDirection: "column",
    "> div": {
      border: 0,

      "&:nth-of-type(-n+2)": {
        borderBottom: 0,
      },

      "&:nth-of-type(2n-1)": {
        borderRight: 0,
      },
    },
  },
});

export const Stat1 = ({ opacity, backgroundColor, padding, iconSize }) => {
  return (
    <GelBoxLayout
      vertical
      gap="small"
      alignmentIndividual="center"
      style={{
        justifySelf: "center",
        backgroundColor: backgroundColor,
        padding: padding,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <GelIcon
          name="PeopleMale"
          brand
          svgCode={PeopleMale}
          width={iconSize}
          color={getGelTokens().global.themeColorIconDefault}
          style={{ opacity: opacity, transition: `all 0.3s ease-in-out` }}
        ></GelIcon>
        <GelIcon
          name="PeopleMale"
          brand
          svgCode={PeopleMale}
          width={iconSize}
          color={getGelTokens().global.themeColorIconDefault}
          style={{ opacity: opacity, transition: `all 0.3s ease-in-out` }}
        ></GelIcon>
        <GelIcon
          name="PeopleMale"
          brand
          svgCode={PeopleMale}
          width={iconSize}
          color={getGelTokens().global.themeColorIconDefault}
          style={{
            opacity: opacity,
            transitionDelay: 2,
            transitionDuration: 0.3,
            transitionTimingFunction: "ease-in-out",
            transitionProperty: "all",
          }}
        ></GelIcon>
        <GelIcon
          name="PeopleMale"
          brand
          svgCode={PeopleMale}
          width={iconSize}
          color={getGelTokens().global.themeColorIconDefault}
          style={{
            opacity: opacity,
            transitionDelay: 4,
            transitionDuration: 0.3,
            transitionTimingFunction: "ease-in-out",
            transitionProperty: "all",
          }}
        ></GelIcon>
        <GelIcon
          name="PeopleMale"
          brand
          svgCode={PeopleMale}
          width={iconSize}
          color={getGelTokens().global.themeColorTextInverse}
          style={{
            opacity: opacity,
            transitionDelay: 6,
            transitionDuration: 0.3,
            transitionTimingFunction: "ease-in-out",
            transitionProperty: "all",
          }}
        ></GelIcon>
      </div>
      <div style={{ opacity: opacity, transition: `all 0.3s ease-in-out` }}>
        <GelHeading3 textAlign="center" inverse marginTop={16} marginBottom={16}>
          4 in 5 Australians
        </GelHeading3>
        <GelParagraph textAlign="center" inverse>
          Believe that vigilance around self-checking their skin is an important part of early
          detection in skin safety
        </GelParagraph>
      </div>
    </GelBoxLayout>
  );
};

export const Stat2 = ({ inverse, value }) => {
  return (
    <GelBoxLayout vertical>
      <Pie value={value} inverse={inverse} color={getGelTokens().global.themeColorTextInverse} />
      <GelParagraph textAlign="center" inverse={inverse}>
        Cannot name the four signs to look out for when self-checking their skin for signs of skin
        cancer
      </GelParagraph>
    </GelBoxLayout>
  );
};

export const Acronym = ({ title, children }) => {
  const StyledHeading = styled.div`
    > :first-letter {
      color: ${getGelTokens().brand.brandColorPrimary1};
    }
  `;

  const Container = styled.div`
    margin-top: 32px;
    margin-bottom: 32px;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 200px;
  `;
  return (
    <Container>
      <StyledHeading>
        <GelHeading3
          as="div"
          textAlign="center"
          inverse
          marginBottom={getGelTokens().global.sizeBaseX6}
        >
          {title}
        </GelHeading3>
      </StyledHeading>

      <GelParagraph inverse textAlign="center">
        {children}
      </GelParagraph>
    </Container>
  );
};
